import React from "react";
import tw from "tailwind-styled-components";
import { FaReceipt } from "react-icons/fa";

const OrderSummary = ({ order, shippingAddress, formatCurrency }) => {
  const getStatusColor = (status) => {
    if (status === "paid") return "bg-green-50 text-green-700 border-green-200";
    if (status === "failed") return "bg-red-50 text-red-700 border-red-200";
    return "bg-yellow-50 text-yellow-700 border-yellow-200";
  };

  return (
    <SummaryCard>
      <Header>
        <Title>
          <FaReceipt className="text-kuality-blue" /> Order Summary
        </Title>
      </Header>

      <Content>
        <Section>
          <SectionTitle>Delivery Address</SectionTitle>
          {shippingAddress ? (
            <Address>
              <p className="font-semibold text-gray-700">
                {shippingAddress?.first_name} {shippingAddress?.last_name}
              </p>
              <p>{shippingAddress?.address_line_1}</p>
              {shippingAddress?.address_line_2 && (
                <p>{shippingAddress?.address_line_2}</p>
              )}
              <p>
                {shippingAddress?.city}, {shippingAddress?.state}{" "}
                {shippingAddress?.postal_code &&
                  `-${shippingAddress?.postal_code}`}
              </p>
            </Address>
          ) : (
            <p className="text-sm text-gray-500 italic">
                No shipping address provided.
            </p>
          )}
        </Section>

        <Divider />

        <Section className="space-y-2">
          <SectionTitle>Payment Information</SectionTitle>
          <Row>
            <Label>Payment Method</Label>
            <Badge className="bg-gray-100 text-gray-800 border-transparent uppercase">
              {order.payment_method
                ? order.payment_method.replace(/_/g, " ") // Replace underscores with spaces
                : "N/A"}
            </Badge>
          </Row>
          <Row>
            <Label>Payment Status</Label>
            <Badge className={getStatusColor(order.payment_status)}>
              {order.payment_status || "Pending"}
            </Badge>
          </Row>
        </Section>

        <Divider />

        <Section className="space-y-2">
          <Row>
            <Label>Subtotal</Label>
            <Value>{formatCurrency(order.subtotal || 0)}</Value>
          </Row>

          {Number(order.tax_amount) > 0 && (
            <Row>
              <Label>Tax</Label>
              <Value>{formatCurrency(order.tax_amount)}</Value>
            </Row>
          )}

          <Row>
            <Label>Shipping Fee</Label>
            <Value>
              {Number(order.shipping_amount) === 0 ? (
                <span className="text-kuality-blue">Free</span>
              ) : (
                formatCurrency(order.shipping_amount)
              )}
            </Value>
          </Row>

          {Number(order.discount_amount) > 0 && (
            <Row>
              <Label>Discount</Label>
              <Value className="text-kuality-blue">-{formatCurrency(order.discount_amount)}</Value>
            </Row>
          )}
        </Section>

        <Divider className="border-solid" />

        <Total>
          <span className="text-base font-bold text-gray-900">Total</span>
          <TotalValue>{formatCurrency(order.total_amount)}</TotalValue>
        </Total>
      </Content>
    </SummaryCard>
  );
};

// Styled Components
const SummaryCard = tw.div`
    bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden sticky top-0 my-4
`;

const Header = tw.div`
    p-4 border-b border-gray-100 bg-gray-50/50
`;

const Title = tw.h3`
    font-semibold text-gray-800 flex items-center gap-2
`;

const Content = tw.div`
    p-4 space-y-3
`;

const Section = tw.div`
    flex flex-col
`;

const SectionTitle = tw.h4`
    text-sm font-semibold text-gray-500 my-1
`;

const Address = tw.div`
    text-sm text-gray-600 leading-relaxed
`;

const Divider = tw.hr`
    border-dashed border-gray-200
`;

const Row = tw.div`
    flex justify-between items-center text-sm
`;

const Label = tw.span`
    text-gray-600
`;

const Value = tw.span`
    font-medium text-gray-900
`;

const Badge = tw.span`
    font-medium capitalize px-2 py-0.5 rounded text-xs border
`;

const Total = tw.div`
    flex justify-between items-center pt-1
`;

const TotalValue = tw.span`
    text-xl font-bold text-kuality-blue
`;

export default OrderSummary;
